import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Modal, Select } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import type { Group, User } from "@/lib/types";

export function useGroups() {
  return useQuery({ queryKey: ["admin", "groups"], queryFn: () => api.get<Group[]>("/api/admin/groups") });
}

export default function Users() {
  const qc = useQueryClient();
  const me = useMe().data!;
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: () => api.get<User[]>("/api/admin/users") });
  const [editing, setEditing] = useState<User | "new" | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  return (
    <Card
      title="Users"
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={16} /> Add user
        </Button>
      }
    >
      <ErrorText error={remove.error} />
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {users.data?.map((u) => (
          <li key={u.id} className="flex items-center justify-between gap-4 py-2.5">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                {u.name} <span className="font-normal text-zinc-500">@{u.username}</span>
                {u.role === "admin" && <Badge tone="indigo">admin</Badge>}
                {u.disabled && <Badge tone="red">disabled</Badge>}
                {u.groups.map((g) => (
                  <Badge key={g.id}>{g.name}</Badge>
                ))}
              </p>
              {u.email && <p className="text-xs text-zinc-500">{u.email}</p>}
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => setEditing(u)} aria-label="Edit">
                <Pencil size={16} />
              </Button>
              {u.id !== me.id && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete"
                  onClick={() => confirm(`Delete ${u.username} and all their data?`) && remove.mutate(u.id)}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {editing && <UserForm user={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function UserForm({ user, onClose }: { user: User | null; onClose: () => void }) {
  const qc = useQueryClient();
  const groups = useGroups();
  const [form, setForm] = useState({
    username: user?.username ?? "",
    name: user?.name ?? "",
    email: user?.email ?? "",
    password: "",
    role: user?.role ?? "user",
    disabled: user?.disabled ?? false,
    group_ids: user?.groups.map((g) => g.id) ?? [],
  });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, email: form.email || null, password: form.password || undefined };
      return user ? api.patch(`/api/admin/users/${user.id}`, body) : api.post("/api/admin/users", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      onClose();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  const toggleGroup = (id: string) =>
    setForm({ ...form, group_ids: form.group_ids.includes(id) ? form.group_ids.filter((g) => g !== id) : [...form.group_ids, id] });

  return (
    <Modal open onClose={onClose} title={user ? `Edit ${user.username}` : "Add user"}>
      <form className="space-y-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Username">
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
          </Label>
          <Label label="Display name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Label>
        </div>
        <Label label="Email">
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Label>
        <Label label={user ? "New password" : "Password"} hint={user ? "Leave empty to keep the current one." : undefined}>
          <Input type="password" autoComplete="new-password" minLength={8} required={!user} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Label>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Role">
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as User["role"] })}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Select>
          </Label>
          <div className="flex items-end pb-2">
            <Checkbox label="Disabled" checked={form.disabled} onChange={(e) => setForm({ ...form, disabled: e.target.checked })} />
          </div>
        </div>
        {!!groups.data?.length && (
          <div className="space-y-1.5">
            <span className="text-sm font-medium">Groups</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {groups.data.map((g) => (
                <Checkbox key={g.id} label={g.name} checked={form.group_ids.includes(g.id)} onChange={() => toggleGroup(g.id)} />
              ))}
            </div>
          </div>
        )}
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
